import { AccountBalance } from "@mui/icons-material"
import { Box, Button, Paper } from "@mui/material"
import Stack from "@mui/material/Stack"
import { DataGrid, GridColDef, GridRowsProp } from "@mui/x-data-grid"
import { useMeasure } from "@uidotdev/usehooks"
import useModalTrigger from "magik-react-hooks/useModalTrigger"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import CustomToolbar from "../components/CustomToolbar"
import DialogConfirm from "../components/DialogConfirm"
import DialogForm from "../components/DialogForm"
import EnteForm from "../components/forms/EnteForm"
import IconButtonDelete from "../components/IconButtonDelete"
import IconButtonEdit from "../components/IconButtonEdit"
import Layout from "../components/Layout"
import { PageTitle } from "../components/PageTitle"
import Paginator from "../components/Paginator"
import { useEnteCreate, useEnteDelete, useEnteUpdate, useEntiList } from "../hooks/enti"
import { useQsFilters } from "../hooks/filters"
import useUserCanWriteAndDelete from "../hooks/permissions"
import { useSnackbarMessage } from "../hooks/snackbar"
import { Ente } from "../types"
import { PAGE_SIZE_OPTIONS, usePageSize } from "../hooks/pageSize"

const initFilters = (params: URLSearchParams) => ({
  page: Number(params.get("page") ?? 1),
  search: params.get("search") ?? "",
  ordering: params.get("ordering") ?? "nome",
})

export default function Enti() {
  const pageSize = usePageSize()
  const { filters, setFilters, setFiltersDebounced, uiFilters } = useQsFilters(initFilters)
  const { t } = useTranslation()
  const userCanWriteAndDelete = useUserCanWriteAndDelete()

  const columns: GridColDef[] = useMemo(() => {
    return [
      {
        field: "nome",
        headerName: t("ente.nome"),
        flex: 1,
      },
      {
        field: "id",
        headerName: t("general.azioni"),
        flex: 1,
        display: "flex",
        align: "right",
        headerAlign: "right",
        hideSortIcons: true,
        sortable: false,
        renderCell: (params) => (
          <>
            <IconButtonEdit onClick={() => editModalActions.open(params.row)} />
            {userCanWriteAndDelete && <IconButtonDelete onClick={() => deleteModalActions.open(params.row)} />}
          </>
        ),
      },
    ]
  }, [t])

  const { data, isLoading } = useEntiList({
    page: String(filters.page),
    page_size: String(pageSize),
    search: filters.search,
    ordering: filters.ordering,
  })

  const { mutateAsync: create } = useEnteCreate()
  const { mutateAsync: update } = useEnteUpdate()
  const { mutateAsync: remove } = useEnteDelete()

  const numPages = useMemo(() => {
    if (!data) return 1
    return Math.ceil(data.count / pageSize)
  }, [data, pageSize])

  const [ref, { height }] = useMeasure()
  const [toolbarRef, { height: toolbarHeight }] = useMeasure()
  const tableHeight = useMemo(() => {
    if (!height || !toolbarHeight) return
    return height - toolbarHeight
  }, [height, toolbarHeight])

  const [createModalState, createModalActions] = useModalTrigger()
  const [editModalState, editModalActions] = useModalTrigger()
  const [deleteModalState, deleteModalActions] = useModalTrigger()

  const showMessage = useSnackbarMessage()

  const filtersCount = useMemo(() => {
    let count = 0

    return count
  }, [])

  return (
    <Layout padding>
      <Stack sx={{ height: "100%" }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <PageTitle
            title={t("ente.titolo_lista")}
            breadcrumbs={[
              {
                label: t("ente.enti"),
                iconType: AccountBalance,
              },
            ]}
          />
          {userCanWriteAndDelete && (
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                createModalActions.open()
              }}
            >
              {t("ente.cta_aggiungi")}
            </Button>
          )}
        </Box>
        <Box flexGrow={1}>
          <Paper sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
            <CustomToolbar
              count={data?.count}
              search={uiFilters.search}
              setSearch={(search) => setFiltersDebounced({ ...filters, search, page: 1 })}
              clearFilters={() => setFilters({ search: "", page: 1 })}
              filtersCount={filtersCount}
              ref={toolbarRef}
            />
            <div
              style={{
                flexGrow: 1,
                overflow: "scroll",
                height: tableHeight ?? undefined,
              }}
              ref={ref}
            >
              {height && (
                <DataGrid
                  disableColumnMenu
                  disableColumnResize
                  // density="compact"
                  pageSizeOptions={PAGE_SIZE_OPTIONS}
                  paginationModel={{ page: 0, pageSize }}
                  sx={{
                    padding: 2,
                    "&.MuiDataGrid-root": {
                      border: "none",
                    },
                  }}
                  localeText={{
                    noRowsLabel: t("general.nessun_dato_disponibile"),
                  }}
                  loading={isLoading}
                  slots={{
                    footer: () => (
                      <Paginator
                        numPages={numPages}
                        page={filters.page}
                        setPage={(page) =>
                          setFilters({
                            ...filters,
                            page,
                          })
                        }
                      />
                    ),
                    toolbar: null,
                  }}
                  rows={data?.results ?? ([] as GridRowsProp)}
                  columns={columns}
                  onSortModelChange={(model) => {
                    if (model.length > 0) {
                      const sortSign = model[0].sort === "asc" ? "" : "-"
                      const newOrdering = `${sortSign}${model[0].field}`
                      setFilters({ ordering: newOrdering, page: 1 })
                    }
                  }}
                />
              )}
            </div>
          </Paper>
        </Box>
      </Stack>

      <DialogForm<Ente>
        open={createModalState.isOpen}
        handleClose={createModalActions.close}
        title={t("ente.cta_aggiungi")}
        FormComponent={EnteForm}
        save={(values: Ente) => {
          return create(values).then(() => {
            createModalActions.close()
          })
        }}
      />
      <DialogForm<Ente>
        open={editModalState.isOpen}
        handleClose={editModalActions.close}
        title={t("ente.cta_modifica")}
        FormComponent={EnteForm}
        save={(values: Ente) => {
          return update(values).then(() => {
            editModalActions.close()
            showMessage(t("ente.esito_modifica_ok"))
          })
        }}
        item={editModalState.value}
        hasCancel
      />
      <DialogConfirm
        open={deleteModalState.isOpen}
        handleClose={deleteModalActions.close}
        title={t("ente.cta_elimina")}
        subtitle={
          <span>
            {t("ente.cta_subtitle_elimina")} <b>{deleteModalState.value?.nome}</b> ?
          </span>
        }
        textConfirm={t("general.elimina")}
        onConfirm={() => {
          return remove(deleteModalState.value.id).then(() => {
            deleteModalActions.close()
            showMessage(t("ente.esito_elimina_ok"))
          })
        }}
      />
    </Layout>
  )
}
